/**
 * Contrato público do sorteio do credenciamento (frente F08) — o que a API
 * `bff/v1/sorteios-publicos` devolve, sem sessão. Sem React.
 *
 * Nada aqui traz placa nem e-mail: a leitura pública só expõe protocolo, nome
 * da entidade e situação (D-07). Os tipos seguem a seção 2.5 do contrato da
 * frente; a área interna (`operacional/sorteios`) reaproveita os rótulos e os
 * tipos comuns daqui.
 */

import type { StatusVariant } from '@/ui/StatusBadge';

export const MODALIDADES = ['CONVENCIONAL', 'ACESSIVEL', 'ESPECIAL', 'CRECHE'] as const;
export type Modalidade = (typeof MODALIDADES)[number];
export type StatusSorteio = 'PREPARADO' | 'EXECUTADO' | 'PUBLICADO';
export type Situacao = 'CONTEMPLADO' | 'SUPLENTE';
export type TipoDeEvento =
  'PREPARADO' | 'EXECUTADO' | 'EXECUCAO_RECUSADA' | 'PUBLICADO' | 'SUPLENTE_CHAMADO';

export interface Pagina<T> {
  itens: T[];
  total: number;
  pagina: number;
  tamanho: number;
}

export interface LinhaPublica {
  dreNome: string;
  dreCodigo: string;
  modalidade: Modalidade;
  posicao: number;
  protocolo: string;
  nome: string;
  situacao: Situacao;
  posicaoEspera: number | null;
  /** Suplente chamado da lista de espera. */
  chamadoEm: string | null;
}

export interface SorteioPublicoResumo {
  id: string;
  cicloNome: string;
  status: StatusSorteio;
  preparadoEm: string;
  executadoEm: string | null;
  publicadoEm: string | null;
  totalParticipantes: number;
  totalVagas: number;
}

export interface EstratoPublico {
  /**
   * Identificador da DRE, o valor que `GET /:id/classificacao?dreId=` espera.
   * Não está na seção 2.5 do contrato (lá o estrato público só tem código e
   * nome), mas sem ele a classificação pública não se filtra por estrato —
   * pedido registrado no README desta pasta.
   */
  dreId?: string;
  dreCodigo: string;
  dreNome: string;
  modalidade: Modalidade;
  vagas: number;
  participantes: number;
  contemplados: number;
  suplentes: number;
}

export interface SorteioPublico extends SorteioPublicoResumo {
  algoritmo: string;
  universoHash: string;
  sementeHash: string;
  sementeRevelada: string | null;
  resultadoHash: string | null;
  publicacaoReferencia: string | null;
  trilhaUltimoHash: string;
  estratos: EstratoPublico[];
  chamadas: {
    dreNome: string;
    modalidade: Modalidade;
    protocolo: string;
    posicaoEspera: number;
    em: string;
  }[];
}

export interface DadosDeVerificacao {
  sorteioId: string;
  algoritmo: string;
  status: StatusSorteio;
  sementeHash: string;
  sementeRevelada: string | null;
  universoHash: string;
  resultadoHash: string | null;
  /** Linhas canônicas, na ordem do universo. */
  linhas: string[];
  resultado: {
    dreCodigo: string;
    modalidade: Modalidade;
    posicao: number;
    protocolo: string;
    situacao: Situacao;
  }[];
  trilha: { seq: number; tipo: string; hashAnterior: string; hash: string; material: string }[];
}

export interface VerificacaoDoSorteio {
  confere: boolean;
  sementeConfere: boolean | null;
  universoConfere: boolean;
  resultadoConfere: boolean | null;
  trilhaIntegra: boolean;
  divergencias: string[];
  verificadoEm: string;
}

export interface FiltroDaClassificacao {
  /** `dreId|modalidade` do estrato escolhido; vazio = todos os estratos. */
  estrato: string;
  situacao: '' | Situacao;
  texto: string;
}

export const ROTULO_MODALIDADE: Record<Modalidade, string> = {
  CONVENCIONAL: 'TEG comum',
  ACESSIVEL: 'Acessível',
  ESPECIAL: 'Especial',
  CRECHE: 'Creche',
};

export function rotuloModalidade(m: string | null | undefined): string {
  return m ? (ROTULO_MODALIDADE[m as Modalidade] ?? m) : '—';
}

export const ROTULO_STATUS_SORTEIO: Record<StatusSorteio, string> = {
  PREPARADO: 'Preparado, aguardando execução',
  EXECUTADO: 'Executado',
  PUBLICADO: 'Publicado',
};

export const VARIANTE_STATUS_SORTEIO: Record<StatusSorteio, StatusVariant> = {
  PREPARADO: 'pendente',
  EXECUTADO: 'processado',
  PUBLICADO: 'concluida',
};

export const ROTULO_EVENTO: Record<TipoDeEvento, string> = {
  PREPARADO: 'Sorteio preparado (compromisso registrado)',
  EXECUTADO: 'Sorteio executado',
  EXECUCAO_RECUSADA: 'Tentativa de execução recusada',
  PUBLICADO: 'Publicação oficial registrada',
  SUPLENTE_CHAMADO: 'Suplente chamado',
};

export function rotuloEvento(tipo: string): string {
  return ROTULO_EVENTO[tipo as TipoDeEvento] ?? tipo;
}

/** "Contemplado" ou "Suplente nº X". */
export function rotuloSituacao(situacao: Situacao, posicaoEspera: number | null): string {
  if (situacao === 'CONTEMPLADO') return 'Contemplado';
  return posicaoEspera ? `Suplente nº ${posicaoEspera}` : 'Suplente';
}

export function varianteSituacao(situacao: Situacao, chamadoEm: string | null): StatusVariant {
  if (situacao === 'CONTEMPLADO') return 'aprovado';
  return chamadoEm ? 'ativo' : 'pendente';
}

/** Data e hora em São Paulo. */
export function dataHora(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        dateStyle: 'short',
        timeStyle: 'short',
      });
}

export function data(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' });
}

/** Começo e fim de um hash, para caber na tela; o valor inteiro vai para a cópia. */
export function abreviarHash(hash: string | null | undefined): string {
  if (!hash) return '—';
  return hash.length > 20 ? `${hash.slice(0, 10)}…${hash.slice(-6)}` : hash;
}

/** Chave de um estrato no seletor. */
export function chaveDoEstrato(dre: string, modalidade: string): string {
  return `${dre}|${modalidade}`;
}

/** Filtro da classificação → query string (`dreId`, `modalidade`, `situacao`, `texto`). */
export function consultaDaClassificacao(
  f: FiltroDaClassificacao,
  pagina: number,
  tamanho: number,
): string {
  const q = new URLSearchParams();
  if (f.estrato) {
    const [dreId, modalidade] = f.estrato.split('|');
    if (dreId) q.set('dreId', dreId);
    if (modalidade) q.set('modalidade', modalidade);
  }
  if (f.situacao) q.set('situacao', f.situacao);
  if (f.texto.trim()) q.set('texto', f.texto.trim());
  q.set('pagina', String(pagina));
  q.set('tamanho', String(tamanho));
  return q.toString();
}
